import { IBeneficiary, IStatus } from '@smiles/voebiz-core-beneficiaries/dist/Types/BeneficiaryByDocumentApi.types';
import CheckIcon from 'components/Icons/CheckIcon';
import CrossIcon from 'components/Icons/CrossIcon';
import EditIcon from 'components/Icons/EditIcon';
import Loader from 'components/Loader';
import ModalActivate from 'components/ModalActivate';
import ResponseSummaryModal from 'components/ModalSummary';
import StatusPill from 'components/StatusPill';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { getBeneficiariesByCpf, updateBeneficiary } from 'services/services';
import { capitalize } from 'utils/capitalize';
import { formatDate } from 'utils/formatDate';
import { cpfMask } from 'utils/maskCpf';

interface TableSummaryProps {
  beneficiaryCpf: string;
}

const TableSummary: FC<TableSummaryProps> = ({ beneficiaryCpf }) => {
  const setParams = useSearchParams()[1];

  const { t } = useTranslation(['summary', 'reactivateBeneficiary']);

  const [modalActiveDeactiveOpen, setModalActiveDeactiveOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [openSummaryModal, setOpenSummaryModal] = useState(false);

  const [beneficiaryDetails, setBeneficiaryDetails] = useState<IBeneficiary>();

  const [beneficiary, setBeneficiary] = useState<IBeneficiary>({} as IBeneficiary);

  const isBeneficiaryActive = beneficiaryDetails?.status === 'Active';

  const handleOpenModalBeneficiaryDetails = () => {
    if (!beneficiaryDetails) {
      return;
    }

    setBeneficiary(beneficiaryDetails);
    setOpenSummaryModal(true);
  };

  const isFilterModalClosed = !openSummaryModal;

  const redirectSummary = useCallback(() => {
    setParams('');
  }, [setParams]);

  const callGetBeneficiariesByCpf = useCallback(async () => {
    setIsLoading(true);

    getBeneficiariesByCpf(beneficiaryCpf)
      .then(({ data }) => setBeneficiaryDetails(data))
      .catch(() => redirectSummary())
      .finally(() => setIsLoading(false));
  }, [beneficiaryCpf, redirectSummary]);

  const updateStatus = async () => {
    if (!beneficiaryDetails) return;

    setIsLoading(true);

    const payload = {
      document: beneficiaryCpf,
      name: beneficiaryDetails.fullname,
      email: beneficiaryDetails.email,
      birthdate: beneficiaryDetails.birthdate,
      status: beneficiaryDetails.status === 'Active' ? 'Inactive' : ('Active' as IStatus),
    };

    updateBeneficiary(payload)
      .then(({ success }) => setShowSuccessModal(success))
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseModalBeneficiaryDetails = () => {
    setOpenSummaryModal(false);
    callGetBeneficiariesByCpf();
  };

  const onToggleReactivateModal = useCallback((isOpen: boolean) => {
    setModalActiveDeactiveOpen(isOpen);
    setShowSuccessModal(false);
  }, []);

  useEffect(() => {
    document.body.classList.remove('unscrollable');
  }, [isFilterModalClosed]);

  useEffect(() => {
    if (beneficiaryCpf) callGetBeneficiariesByCpf();
  }, [beneficiaryCpf, callGetBeneficiariesByCpf]);

  useEffect(() => {
    document.body.classList.remove('unscrollable');
  }, [modalActiveDeactiveOpen]);

  const inativeBeneficiary = beneficiaryDetails?.status === 'Active' ? <EditIcon /> : null;
  return (
    <>
      <div className="summary-wrapper" data-testid="test-table-summary">
        {isLoading && <Loader />}
        {!isLoading && (
          <>
            <div className="text-beneficiary-summary">
              <h5>{t('summary:title_summary')}</h5>
              <p>{t('summary:subtitle_summary')}</p>
            </div>
            <div className="summary">
              <div className="member-name">
                <div>
                  <p>{beneficiaryDetails && capitalize(beneficiaryDetails.fullname)}</p>
                  {!isMobile && <StatusPill status={isBeneficiaryActive ? 'Active' : 'Inactive'} />}
                </div>
                <div
                  onClick={handleOpenModalBeneficiaryDetails}
                  data-testid="action-open-modal"
                  className="open-modal-summary"
                >
                  {inativeBeneficiary}
                </div>
              </div>
              {isMobile && <StatusPill status={isBeneficiaryActive ? 'Active' : 'Inactive'} />}
              <hr className="summary-divider" />
              <table>
                <thead>
                  <tr className="details-member">
                    <th className="date-inclusion">{t('date_inclusion')}</th>
                    <th className="cpf-member">{t('cpf_member')}</th>
                    <th className="mail-member">{t('email_member')}</th>
                    <th className="brithday-member">{t('birthday_member')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="data-row">
                    <td className="date-inclusion">{formatDate(beneficiaryDetails?.created)}</td>
                    <td className="cpf-member">{beneficiaryDetails && cpfMask(beneficiaryDetails?.cpf)}</td>
                    <td className="mail-member">{beneficiaryDetails?.email}</td>
                    <td className="brithday-member">{formatDate(beneficiaryDetails?.birthdate)}</td>
                  </tr>
                </tbody>
              </table>
              <div className="mobile-table">
                <div className="mobile-table-data">
                  <span>{t('date_inclusion')}</span>
                  <span>{beneficiaryDetails?.created}</span>
                </div>
                <div className="mobile-table-data">
                  <span>{t('cpf_member')}</span>
                  <span>{beneficiaryDetails && cpfMask(beneficiaryDetails?.cpf)}</span>
                </div>
                <div className="mobile-table-data">
                  <span>{t('email_member')}</span>
                  <span>{beneficiaryDetails?.email}</span>
                </div>
                <div className="mobile-table-data">
                  <span>{t('birthday_member')}</span>
                  <span>{beneficiaryDetails?.birthdate}</span>
                </div>
              </div>
            </div>
            <button
              data-testid="deactivate-button"
              type="button"
              className={isBeneficiaryActive ? 'deactivate-button' : 'reactivate-button'}
              onClick={() => setModalActiveDeactiveOpen(true)}
            >
              {isBeneficiaryActive ? t('reactivateBeneficiary:deactivate') : t('reactivateBeneficiary:reactivate')}{' '}
              {t('reactivateBeneficiary:profile')}
              {isBeneficiaryActive ? <CrossIcon /> : <CheckIcon />}
            </button>
          </>
        )}
      </div>
      <ModalActivate
        isOpen={modalActiveDeactiveOpen}
        toggleModal={onToggleReactivateModal}
        isBeneficiaryActive={isBeneficiaryActive}
        onActivate={() => updateStatus()}
        onClose={callGetBeneficiariesByCpf}
        showSuccessModal={showSuccessModal}
        isLoading={isLoading}
      />

      {openSummaryModal && (
        <ResponseSummaryModal
          beneficiaryDetails={beneficiary}
          isOpen={openSummaryModal}
          closeModal={handleCloseModalBeneficiaryDetails}
        />
      )}
    </>
  );
};

export default TableSummary;
